/**
 * Shared constants/helpers for Planning Engine list views (§1E / §4.7).
 */

import { useMemo } from 'react';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import type { PlanDecision, WorkUnitState } from '../../../lib/planningEngineModule.types';

/**
 * Hook — returns the enabled resource type keys from the workspace settings store.
 * Use this in React components instead of the static WORK_PROFILES constant.
 */
export function useWorkProfiles(): string[] {
  // Snapshot once — selecting getEnabledTypes() directly returns a new array
  // every call and trips Zustand into an infinite re-render (React #185).
  return useMemo(
    () => useResourceTypesStore.getState().getEnabledTypes().map((rt) => rt.key),
    [],
  );
}

/**
 * Fallback static list used by legacy code that can't call hooks.
 * Prefer useWorkProfiles() in components.
 */
export const WORK_PROFILES = [
  'engineering', 'design', 'pm', 'data', 'qa', 'devops',
  'research', 'marketing', 'sales', 'support', 'legal', 'finance', 'other', 'unknown',
];

export const RESOURCE_TYPES = WORK_PROFILES;

export const WORK_UNIT_STATES: WorkUnitState[] = [
  'backlog', 'ready', 'selected_for_dev', 'in_progress', 'done', 'blocked', 'cancelled',
];

export function planDecisionClass(decision: PlanDecision): string {
  return decision === 'Included'
    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
    : decision === 'Out - capacity'
      ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
      : decision === 'Blocked'
        ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
        : decision === 'Not Ready'
          ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
          : decision === 'Needs Sizing'
            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
            : 'bg-gray-50 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400';
}

export function exportCsv(filename: string, header: string[], rows: string[][]) {
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Stable key for a dependency's endpoint pair, at either grain.
 *
 * Dependencies can arrive two ways — created in Planning, or materialized from
 * a Prioritization relationship under that edge's own id — so identity has to
 * be the (from, to) pair rather than the row id, or the same link stores twice
 * and renders as two identical rows.
 */
export function dependencyPairKey(fromId: string, toId: string): string {
  return `${fromId}->${toId}`;
}
